import React, { useState, useEffect } from 'react';
import { Switch, Input, Typography, Button, Alert, notification, Spin } from 'antd';
import { CustomModal } from '@/components/CustomModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { buyCredits, updateAutoReloadCredits, getCredits } from '@/api/user';
import {
	InfoCircleOutlined,
	ReloadOutlined,
	PhoneOutlined,
	MessageOutlined,
	CloseOutlined,
	SaveOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getLoggedInUser } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { setCurrentPage } from '@/store/account/slice';
import { UserDashboardSiderItem } from 'src/enums/EUserDashboardSiderItem';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';

const { Title, Text } = Typography;

interface CreditDetails {
	// Voice Credits
	voiceTotalCredits: number;
	voiceFreeCredits: number;
	voiceCredits: number;
	voiceThresholdCredits: number;
	voiceCreditsToReload: number;
	voiceAutoReloadCredits: boolean;

	// SMS Credits
	smsTotalCredits: number;
	smsFreeCredits: number;
	smsCredits: number;
	smsThresholdCredits: number;
	smsCreditsToReload: number;
	smsAutoReloadCredits: boolean;

	// Plan information
	planName?: string;
	freeVoiceCredits?: number;
	freeSMSCredits?: number;

	// Shared date fields for both voice and SMS
	freeCreditsExpiresOn?: string;
	freeCreditsRefillsOn?: string;
	freeCreditsLastRefilledOn?: string;
}

const ManageCredits: React.FC = () => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const dispatch = useAppDispatch();
	const [activeTab, setActiveTab] = useState<string>('voice');
	const [buyCreditsAmount, setBuyCreditsAmount] = useState<number>(10);
	const [buySMSCreditsAmount, setBuySMSCreditsAmount] = useState<number>(10);
	const [modalState, setModalState] = useState({
		isOpen: false,
		mode: 'buy' as 'buy' | 'autoReload',
		type: 'voice' as 'voice' | 'sms',
	});

	// Track changes separately for voice and SMS
	const [voiceHasChanges, setVoiceHasChanges] = useState<boolean>(false);
	const [smsHasChanges, setSmsHasChanges] = useState<boolean>(false);

	// Add state to track recently purchased credits
	const [recentPurchase, setRecentPurchase] = useState<{
		type: 'voice' | 'sms';
		amount: number;
		timestamp: number;
	} | null>(null);

	// New state to track local changes before saving
	const [localSettings, setLocalSettings] = useState({
		voiceAutoReloadCredits: false,
		smsAutoReloadCredits: false,
		voiceCreditsToReload: 0,
		voiceThresholdCredits: 0,
		smsCreditsToReload: 0,
		smsThresholdCredits: 0,
	});

	const [errors, setErrors] = useState({
		voiceCreditsToReload: '',
		voiceThresholdCredits: '',
		smsCreditsToReload: '',
		smsThresholdCredits: '',
	});

	const {
		data: creditDetails,
		refetch,
		isLoading,
		isRefetching,
	} = useQuery<{ success: boolean; data?: CreditDetails; error?: string }>({
		queryKey: ['getUserCredits'],
		queryFn: () => getCredits('Client'),
		enabled: Boolean(
			(loggedInUser?.inboundSubscriptionId &&
				loggedInUser?.inboundSubscriptionStatus !== 'canceled') ||
				(loggedInUser?.outboundSubscriptionId &&
					loggedInUser?.outboundSubscriptionStatus !== 'canceled'),
		),
	});

	// Update local settings when API data is loaded
	useEffect(() => {
		if (creditDetails?.data) {
			resetLocalSettings();
		}
	}, [creditDetails]);

	// Function to reset local settings to match server data
	const resetLocalSettings = () => {
		if (creditDetails?.data) {
			setLocalSettings({
				voiceAutoReloadCredits: creditDetails.data.voiceAutoReloadCredits || false,
				smsAutoReloadCredits: creditDetails.data.smsAutoReloadCredits || false,
				voiceCreditsToReload:
					creditDetails.data.voiceCreditsToReload >= 10
						? creditDetails.data.voiceCreditsToReload
						: 10,
				voiceThresholdCredits: creditDetails.data.voiceThresholdCredits || 0,
				smsCreditsToReload:
					creditDetails.data.smsCreditsToReload >= 10 ? creditDetails.data.smsCreditsToReload : 10,
				smsThresholdCredits: creditDetails.data.smsThresholdCredits || 0,
			});
			setVoiceHasChanges(false);
			setSmsHasChanges(false);
		}
	};

	const updateCreditsMutation = useMutation({
		mutationKey: ['updateUserCredits'],
		mutationFn: updateAutoReloadCredits,
		onSuccess: (_, variables) => {
			notification.success({
				message: 'Credits Updated',
				description: 'Your credit settings have been updated successfully.',
			});

			// Reset the appropriate change flag based on which type was updated
			if ('type' in variables && variables.type === 'sms') {
				setSmsHasChanges(false);
			} else {
				setVoiceHasChanges(false);
			}

			refetch();
		},
		onError: (error: any) => {
			notification.error({
				message: 'Update Failed',
				description: error.message || 'Failed to update credit settings.',
			});
		},
	});

	const buyCreditsMutation = useMutation({
		mutationKey: ['buyCredits'],
		mutationFn: buyCredits,
		onSuccess: (_) => {
			const amount = modalState.type === 'voice' ? buyCreditsAmount : buySMSCreditsAmount;

			// Immediately update the local state with the purchased credits
			if (creditDetails?.data) {
				const updatedData = { ...creditDetails.data };

				if (modalState.type === 'voice') {
					updatedData.voiceCredits = (updatedData.voiceCredits || 0) + amount;
					updatedData.voiceTotalCredits = (updatedData.voiceTotalCredits || 0) + amount;
				} else {
					updatedData.smsCredits = (updatedData.smsCredits || 0) + amount;
					updatedData.smsTotalCredits = (updatedData.smsTotalCredits || 0) + amount;
				}

				// Update the creditDetails with the new data without refetching
				creditDetails.data = updatedData;
			}

			// Set recent purchase info
			setRecentPurchase({
				type: modalState.type,
				amount,
				timestamp: Date.now(),
			});

			notification.success({
				message: 'Credits Purchased',
				description: `${modalState.type === 'voice' ? 'Voice' : 'SMS'} credits purchased successfully. Credits will be added to your account shortly.`,
			});

			// Close modal without resetting changes since buying credits doesn't affect auto-reload settings
			setModalState({ isOpen: false, mode: 'buy', type: 'voice' });
			// Don't refetch as credits are updated via webhook and will take time to reflect in DB
		},
		onError: (error: any) => {
			notification.error({
				message: 'Purchase Failed',
				description: error.message || 'Failed to purchase credits.',
			});
		},
	});

	const handleAutoReloadToggle = (checked: boolean, type: 'voice' | 'sms') => {
		setLocalSettings((prev) => ({
			...prev,
			[type === 'voice' ? 'voiceAutoReloadCredits' : 'smsAutoReloadCredits']: checked,
		}));

		if (type === 'voice') {
			setVoiceHasChanges(true);
		} else {
			setSmsHasChanges(true);
		}
	};

	const handleBuyCredits = () => {
		const amount = modalState.type === 'voice' ? buyCreditsAmount : buySMSCreditsAmount;
		buyCreditsMutation.mutate({
			credits: amount,
			creditsFor: modalState.type,
			userType: 'Client',
		});
	};

	const handleReloadAmountChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		type: 'voice' | 'sms',
	) => {
		const value = Number(e.target.value);
		setLocalSettings((prev) => ({
			...prev,
			[type === 'voice' ? 'voiceCreditsToReload' : 'smsCreditsToReload']: value,
		}));

		if (type === 'voice') {
			setVoiceHasChanges(true);
		} else {
			setSmsHasChanges(true);
		}
	};

	const handleMinBalanceChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		type: 'voice' | 'sms',
	) => {
		const value = Number(e.target.value);
		setLocalSettings((prev) => ({
			...prev,
			[type === 'voice' ? 'voiceThresholdCredits' : 'smsThresholdCredits']: value,
		}));

		if (type === 'voice') {
			setVoiceHasChanges(true);
		} else {
			setSmsHasChanges(true);
		}
	};

	const validateFields = (type: 'voice' | 'sms') => {
		const newErrors = {
			voiceCreditsToReload: '',
			voiceThresholdCredits: '',
			smsCreditsToReload: '',
			smsThresholdCredits: '',
		};

		if (type === 'voice') {
			if (!localSettings.voiceCreditsToReload || localSettings.voiceCreditsToReload < 10) {
				newErrors.voiceCreditsToReload = 'Please enter at least 10 credits to reload';
			}

			if (!localSettings.voiceThresholdCredits || localSettings.voiceThresholdCredits <= 0) {
				newErrors.voiceThresholdCredits = 'Please enter a valid minimum balance';
			}
		} else {
			if (!localSettings.smsCreditsToReload || localSettings.smsCreditsToReload < 10) {
				newErrors.smsCreditsToReload = 'Please enter at least 10 credits to reload';
			}

			if (!localSettings.smsThresholdCredits || localSettings.smsThresholdCredits <= 0) {
				newErrors.smsThresholdCredits = 'Please enter a valid minimum balance';
			}
		}

		setErrors(newErrors);
		return !Object.values(newErrors).some((error) => error !== '');
	};

	const handleSubmit = (type: 'voice' | 'sms') => {
		if (!validateFields(type)) {
			notification.error({
				message: 'Validation Error',
				description: `Please fill in all required fields with valid values for ${type === 'voice' ? 'Voice' : 'SMS'} credits`,
			});
			return;
		}

		// Only send the settings for the specific type
		if (type === 'voice') {
			updateCreditsMutation.mutate({
				autoReloadCredits: localSettings.voiceAutoReloadCredits,
				creditsToReload: localSettings.voiceCreditsToReload,
				thresholdCredits: localSettings.voiceThresholdCredits,
				settingsFor: 'voice',
				userType: 'Client',
			});
		} else {
			updateCreditsMutation.mutate({
				autoReloadCredits: localSettings.smsAutoReloadCredits,
				creditsToReload: localSettings.smsCreditsToReload,
				thresholdCredits: localSettings.smsThresholdCredits,
				settingsFor: 'sms',
				userType: 'Client',
			});
		}
	};

	const renderCreditStatus = (type: 'voice' | 'sms') => {
		const isVoice = type === 'voice';
		// Calculate total credits as the sum of free and purchased credits
		const freeCredits = isVoice
			? (creditDetails?.data?.voiceFreeCredits ?? 0)
			: (creditDetails?.data?.smsFreeCredits ?? 0);
		const paidCredits = isVoice
			? (creditDetails?.data?.voiceCredits ?? 0)
			: (creditDetails?.data?.smsCredits ?? 0);
		// Calculate total as the sum of free and paid credits
		const totalCredits = freeCredits + paidCredits;

		const nextRefillDate = creditDetails?.data?.freeCreditsRefillsOn;

		// Check if there's a recent purchase for this credit type
		const hasRecentPurchase =
			recentPurchase &&
			recentPurchase.type === type &&
			Date.now() - recentPurchase.timestamp < 5 * 60 * 1000; // Show for 5 minutes

		return (
			<div className="p-4 bg-gray-50 rounded-lg mb-4">
				<div className="flex justify-between items-center mb-3">
					<h3 className="text-lg font-medium">{isVoice ? 'Voice Credits' : 'SMS Credits'}</h3>
					<div className="flex items-center gap-3">
						<Button
							type="text"
							icon={<ReloadOutlined />}
							onClick={() => {
								refetch();
								// Clear recent purchase state when manually refreshing
								if (recentPurchase && recentPurchase.type === type) {
									setRecentPurchase(null);
								}
							}}
							loading={isRefetching}
							className="flex items-center text-blue-600"
						>
							Refresh
						</Button>
						{!loggedInUser?.resellerClient && (
							<a
								href="https://www.myaifrontdesk.com/tutorials/how-does-billing-pricing-work-for-my-ai-front-desk"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center text-blue-600 text-sm font-medium"
							>
								<InfoCircleOutlined className="mr-1" />
								<span>Learn More</span>
							</a>
						)}
					</div>
				</div>

				{/* Show recent purchase notification */}
				{hasRecentPurchase && (
					<Alert
						message={`${recentPurchase.amount} ${isVoice ? 'Voice' : 'SMS'} Credits Purchased`}
						description={
							<div>
								<p>
									Your credits have been added to the displayed total. The official update may take
									a few minutes to process.
								</p>
								<p className="mt-1">
									Click the refresh button to check for the latest data from the server.
								</p>
							</div>
						}
						type="info"
						showIcon
						className="mb-4"
						closable
						onClose={() => setRecentPurchase(null)}
					/>
				)}

				{/* Credit Amounts */}
				<div className="grid grid-cols-2 gap-4 mb-4">
					<div className="bg-white p-3 rounded border">
						<div className="text-sm text-gray-500">Free Credits</div>
						<div className="text-2xl font-bold">{freeCredits.toFixed(1)}</div>
						<div className="text-xs text-gray-500 mt-1 flex items-center">
							<ReloadOutlined className="mr-1" style={{ fontSize: '12px' }} />
							Resets on: {nextRefillDate ? dayjs(nextRefillDate).format('MMM D, YYYY') : 'N/A'}
						</div>
					</div>

					<div className="bg-white p-3 rounded border">
						<div className="text-sm text-gray-500">Purchased Credits</div>
						<div className="text-2xl font-bold">{paidCredits.toFixed(1)}</div>
						<div className="text-xs text-gray-500 mt-1 flex items-center">
							<InfoCircleOutlined className="mr-1" style={{ fontSize: '12px' }} />
							Never expires, only consumed when used
						</div>
					</div>
				</div>

				{/* Information Box */}
				<div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
					<h4 className="text-blue-800 font-medium mb-2">Important to Know:</h4>
					<ul className="text-blue-800 space-y-2 list-disc pl-5">
						{isVoice ? (
							<>
								<li>1 credit = 12¢ = -1 minute of receptionist time </li>
								<li>Your free credits resets automatically each month</li>
								<li>Some premium voices may use more than 1 credit per minute</li>
								<li>Most businesses use less than 300 credits per month.</li>
							</>
						) : (
							<>
								<li>1 SMS credit = 12¢ = approximately 6 text messages</li>
								<li>Your free SMS credits resets automatically each month</li>
								<li>SMS costs vary based on the country messages are sent to</li>
							</>
						)}
					</ul>
				</div>

				{/* Total and Buy Button in same row */}
				<div className="flex items-center justify-between mb-3">
					<div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex-grow mr-3">
						<div className="flex justify-between items-center">
							<span className="font-medium">Total Credits Available</span>
							<span className="text-xl font-bold">{totalCredits.toFixed(1)}</span>
						</div>
					</div>

					<Button
						type="primary"
						className="min-w-[180px] rounded-lg border border-blue-100 h-[46px]"
						onClick={() => setModalState({ isOpen: true, mode: 'buy', type })}
					>
						Buy {isVoice ? 'Voice' : 'SMS'} Credits
					</Button>
				</div>
			</div>
		);
	};

	const renderAutoReloadSettings = (type: 'voice' | 'sms') => {
		const isVoice = type === 'voice';
		const autoReloadEnabled = isVoice
			? localSettings.voiceAutoReloadCredits
			: localSettings.smsAutoReloadCredits;
		const creditsToReload = isVoice
			? localSettings.voiceCreditsToReload
			: localSettings.smsCreditsToReload;
		const thresholdCredits = isVoice
			? localSettings.voiceThresholdCredits
			: localSettings.smsThresholdCredits;
		const hasChanges = isVoice ? voiceHasChanges : smsHasChanges;
		const reloadError = isVoice ? errors.voiceCreditsToReload : errors.smsCreditsToReload;
		const thresholdError = isVoice ? errors.voiceThresholdCredits : errors.smsThresholdCredits;

		return (
			<>
				{/* Auto-Reload Feature Box from the image */}
				<div className="bg-amber-50 p-4 rounded-lg border border-amber-100 mb-4">
					<h4 className="text-amber-800 font-medium mb-2">Auto-Reload Feature (Optional):</h4>
					<p className="text-amber-800">
						{isVoice
							? 'If you run out of credits, your receptionist will stop answering calls. To avoid any disruptions, we recommend enabling the Auto-Reload feature, which automatically purchases additional credits once a specified threshold is reached.'
							: 'If you run out of credits, your SMS messages will not be sent. To avoid any disruptions, we recommend enabling the Auto-Reload feature, which automatically purchases additional credits once a specified threshold is reached.'}
					</p>
				</div>

				<div className="border rounded-lg p-4 mb-4">
					<div className="flex items-center justify-between mb-3">
						<h3 className="font-medium">Auto Reload Configuration</h3>
						<Switch
							checked={autoReloadEnabled}
							onChange={(checked) => handleAutoReloadToggle(checked, type)}
						/>
					</div>

					<div className="space-y-3">
						<div>
							<label className="block text-sm mb-1">When your credits reach:</label>
							<Input
								type="number"
								value={thresholdCredits}
								onChange={(e) => handleMinBalanceChange(e, type)}
								min={1}
								className="w-full"
							/>
							{thresholdError && <div className="text-red-500 text-xs mt-1">{thresholdError}</div>}
						</div>

						<div>
							<label className="block text-sm mb-1">Refill the credits back up to:</label>
							<Input
								type="number"
								value={creditsToReload}
								onChange={(e) => handleReloadAmountChange(e, type)}
								min={10}
								className="w-full"
								placeholder="Minimum 10 credits"
							/>
							<Text type="secondary" className="text-xs mt-1">
								Minimum purchase: 10 credits
							</Text>
							{reloadError && <div className="text-red-500 text-xs mt-1">{reloadError}</div>}
						</div>

						{/* Save Button for this specific type */}
						{hasChanges && (
							<Button
								block
								type="primary"
								className="mt-3 bg-blue-500 hover:bg-blue-600 shadow-md transition-all duration-300"
								onClick={() => handleSubmit(type)}
								loading={updateCreditsMutation.isPending}
								icon={<SaveOutlined />}
							>
								Save {isVoice ? 'Voice' : 'SMS'} Credit Settings
							</Button>
						)}

						{hasChanges && (
							<div className="text-xs text-blue-600 mt-1 text-center">
								* You have unsaved changes. Click to save.
							</div>
						)}
					</div>
				</div>
			</>
		);
	};

	const handleCloseModal = () => {
		// Reset any unsaved changes when modal is closed
		resetLocalSettings();
		setModalState({ isOpen: false, mode: 'buy', type: 'voice' });
	};

	// Function to enable auto-reload and close modal without resetting changes
	const handleEnableAutoReload = () => {
		handleAutoReloadToggle(true, modalState.type);
		setModalState({ isOpen: false, mode: 'buy', type: 'voice' });
	};

	const renderBuyCreditsModal = () => {
		const isVoice = modalState.type === 'voice';

		return (
			<CustomModal
				title={
					<div className="flex items-center">
						<span>
							{modalState.mode === 'buy' ? 'Buy' : 'Auto Reload'} {isVoice ? 'Voice' : 'SMS'}{' '}
							Credits
						</span>
					</div>
				}
				width={700}
				isModalOpen={modalState.isOpen}
				cancelAction={handleCloseModal}
				footer={null}
				closeIcon={<CloseOutlined />}
			>
				{modalState.mode === 'buy' ? (
					<div className="space-y-6">
						<div className="bg-gray-50 p-4 rounded-lg">
							<div className="flex items-center gap-2 mb-4">
								<div
									className={`w-8 h-8 rounded-full flex items-center justify-center ${isVoice ? 'bg-blue-500' : 'bg-purple-500'}`}
								>
									{isVoice ? (
										<PhoneOutlined className="text-white" />
									) : (
										<MessageOutlined className="text-white" />
									)}
								</div>
								<Text strong className="text-lg">
									{isVoice ? 'Voice Credits' : 'SMS Credits'}
								</Text>
							</div>

							<Text className="block mb-4">
								{isVoice
									? '1 voice credit = 12 cents = approximately 1 minute of calls based on the voice provider used'
									: '1 SMS credit = 12 cents = approximately 6 text messages but it can vary based on the country it is sent to'}
							</Text>

							<div className="bg-white p-4 rounded-lg border border-gray-100">
								<Text strong className="block mb-2">
									How many credits would you like to buy?
								</Text>
								<Input
									type="number"
									value={isVoice ? buyCreditsAmount : buySMSCreditsAmount}
									onChange={(e) =>
										isVoice
											? setBuyCreditsAmount(Number(e.target.value))
											: setBuySMSCreditsAmount(Number(e.target.value))
									}
									className="w-full mb-2"
									min={10}
									placeholder="Enter amount (minimum 10 credits)"
									suffix={`${isVoice ? 'voice' : 'SMS'} credits`}
								/>

								{(isVoice ? buyCreditsAmount : buySMSCreditsAmount) > 0 && (
									<Text type="secondary">
										Estimated cost: $
										{(isVoice ? buyCreditsAmount * 0.12 : buySMSCreditsAmount * 0.12).toFixed(2)}
									</Text>
								)}

								<Text type="secondary" className="text-xs mt-1">
									{' '}
									Minimum purchase: 10 credits
								</Text>
							</div>
						</div>

						<Alert
							message="Need a constant supply of credits?"
							description="Turn on auto-reload in the settings to ensure your service never gets interrupted."
							type="info"
							showIcon
						/>

						<div className="flex justify-end gap-4 mt-6">
							<Button onClick={handleCloseModal} className="min-w-[100px]">
								Cancel
							</Button>
							<Button
								type="primary"
								onClick={handleBuyCredits}
								loading={buyCreditsMutation.isPending}
								disabled={
									isVoice
										? !buyCreditsAmount || buyCreditsAmount < 10
										: !buySMSCreditsAmount || buySMSCreditsAmount < 10
								}
								className="min-w-[120px]"
							>
								Buy Credits
							</Button>
						</div>
					</div>
				) : (
					<div className="space-y-6">
						<Alert
							message={`Enable ${isVoice ? 'Voice' : 'SMS'} Auto Reload`}
							description={`When your ${
								isVoice ? 'voice' : 'SMS'
							} credits fall below your set threshold, we'll automatically purchase more credits to ensure uninterrupted service.`}
							type="info"
							showIcon
						/>

						<div className="bg-gray-50 p-4 rounded-lg">
							<Title level={5}>Auto Reload Invoice Information</Title>
							<div className="space-y-2 mt-2">
								<Text>• Last invoice status: Pending</Text>
								<Text>• Next retry date: Placeholder</Text>
								<Text>• Number of retries: Placeholder</Text>
							</div>
						</div>

						<Alert
							message="Important information"
							description={
								<ul className="list-disc pl-5 mt-2">
									<li>Credits will be automatically purchased when your balance is low</li>
									<li>You can adjust or disable auto-reload at any time</li>
									<li>You'll be notified via email for each auto-reload transaction</li>
									<li>If payment fails, we'll retry up to 3 times before disabling auto-reload</li>
								</ul>
							}
							type="warning"
							showIcon
						/>

						<div className="flex justify-end gap-4">
							<Button onClick={handleCloseModal} className="min-w-[100px]">
								Cancel
							</Button>
							<Button type="primary" onClick={handleEnableAutoReload} className="min-w-[120px]">
								Enable Auto Reload
							</Button>
						</div>
					</div>
				)}
			</CustomModal>
		);
	};

	return (
		<Spin spinning={isLoading || isRefetching}>
			<div className="w-full relative">
				{/* Add overlay when there's no subscription */}
				{((!loggedInUser?.inboundSubscriptionId && !loggedInUser?.outboundSubscriptionId) ||
					(loggedInUser?.inboundSubscriptionStatus === 'canceled' &&
						loggedInUser?.outboundSubscriptionStatus === 'canceled')) && (
					<div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 rounded-lg" />
				)}

				{/* Tab Navigation */}
				<div className="flex mb-4 border-b">
					<button
						className={`px-4 py-2 font-medium ${activeTab === 'voice' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
						onClick={() => setActiveTab('voice')}
					>
						<div className="flex items-center">
							<PhoneOutlined className="mr-2" />
							Voice Credits
						</div>
					</button>
					<button
						className={`px-4 py-2 font-medium ${activeTab === 'sms' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
						onClick={() => setActiveTab('sms')}
					>
						<div className="flex items-center">
							<MessageOutlined className="mr-2" />
							SMS Credits
						</div>
					</button>
				</div>

				{/* Add subscription message when there's no subscription */}
				{((!loggedInUser?.inboundSubscriptionId && !loggedInUser?.outboundSubscriptionId) ||
					(loggedInUser?.inboundSubscriptionStatus === 'canceled' &&
						loggedInUser?.outboundSubscriptionStatus === 'canceled')) && (
					<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 text-center">
						<div className="bg-white p-8 rounded-lg shadow-lg max-w-md">
							<h3 className="text-xl font-semibold text-gray-800 mb-4">No Active Subscription</h3>
							<p className="text-gray-600 mb-6">
								Subscribe to our plans to access credit management features and start using our
								services.
							</p>
							<div className="flex justify-center gap-4">
								<Button
									type="primary"
									size="large"
									onClick={() => {
										dispatch(
											loggedInUser?.userType === 'SubAccount'
												? setCurrentPage(ReceptionistDashboardSiderItem.RESELLER_PLAN)
												: setCurrentPage(UserDashboardSiderItem.SUBSCRIPTION),
										);
									}}
									icon={<PlusOutlined />}
								>
									Subscribe Now
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Credit Information */}
				{activeTab === 'voice' ? (
					<>
						{/* Information Box for Voice Credits */}
						<div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-4">
							<p className="text-green-800 mb-2">
								Your {creditDetails?.data?.planName || 'NA'} Plan includes{' '}
								{creditDetails?.data?.freeVoiceCredits || 200} free credits ($
								{((creditDetails?.data?.freeVoiceCredits || 200) * 0.12).toFixed(2)} value) each
								month
							</p>
						</div>
						{renderCreditStatus('voice')}
						{renderAutoReloadSettings('voice')}
					</>
				) : (
					<>
						{/* Information Box for SMS Credits */}
						<div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-4">
							<p className="text-green-800 mb-2">
								Your {creditDetails?.data?.planName || 'NA'} Plan includes{' '}
								{creditDetails?.data?.freeSMSCredits || 100} free SMS credits ($
								{((creditDetails?.data?.freeSMSCredits || 100) * 0.12).toFixed(2)} value) each month
							</p>
						</div>
						{renderCreditStatus('sms')}
						{renderAutoReloadSettings('sms')}
					</>
				)}

				{renderBuyCreditsModal()}
			</div>
		</Spin>
	);
};

export default ManageCredits;
